/**
 * Control Activity Item Page
 * 
 * Full-page version of Control Activity form (Create/Edit)
 * Replaces the drawer version with a dedicated page and tab-based interface
 * 
 * Features:
 * - Tab 1: Control Description fields
 * - Tab 2: Testing fields
 * - Fixed bottom buttons visible on both tabs
 * - Stay on page after save (don't navigate away)
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Input, Select, Switch, Button, message, DatePicker, Upload, Tabs, Dropdown, Typography, Checkbox, Tooltip } from 'antd';
import type { CustomTagProps } from 'rc-select/lib/BaseSelect';
import { LoadingOutlined, UploadOutlined, LeftOutlined, RightOutlined, InfoCircleOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Inject,
  Page,
} from '@syncfusion/ej2-react-grids';
import ChevronDownIcon from '../../../assets/icons/ic-chevron-down.svg?react';
import ChevronRightIcon from '../../../assets/icons/ic-chevron-right.svg?react';
import RankingIcon from '../../../assets/icons/ic-ranking.svg?react';
import ObjectivesIcon from '../../../assets/icons/ic-objectives.svg?react';
import { RootState, AppDispatch } from '../../../store';
import {
  createControl,
  updateControl,
  getControlActivities,
  frequencyToApiEnum,
  CreateControlRequest,
  UpdateControlRequest,
  convertEffectiveness,
  convertInterimCompleted,
  convertFinalCompleted,
  ControlResponse,
  getAuditors,
} from '../../../services/controlActivitiesService';
import {
  searchCycles,
  searchProcesses,
  CycleItem,
  ProcessItem,
} from '../../../services/hierarchyService';
import { uploadControlTestSheet, downloadFile } from '../../../services/fileService';
import { setControlActivities, setAuditors } from '../../../store/slices/controlActivitiesSlice';
import { clearHierarchyData } from '../../../store/slices/hierarchySlice';
import { clearGapsData } from '../../../store/slices/gapsSlice';
import { setApplications } from '../../../store/slices/auditPlanSlice';
import { getApplications } from '../../../services/auditPlanService';
import {
  getObjectives,
  Objective,
  parseObjectives,
} from '../../../services/riskAssessmentService';
import CloseCircleIcon from '../../../assets/icons/ic-close-circle.svg?react';
import LinkIcon from '../../../assets/icons/ic-link.svg?react';
import ConfirmModal from '../../../components/common/ConfirmModal';
import { ALLOWED_DOCUMENT_EXTENSIONS } from '../../../utils/constants';

const { TextArea } = Input;

// ==================== INTERFACES ====================

export interface ControlActivityFormData {
  // Control Description fields
  what: string;
  who: string;
  why: string;
  how: string;
  when: string;
  evidence: string;
  category: string;
  controlType: string;
  isKeyControl: boolean;
  controlOwner: string;
  isGapCompensating: boolean;

  // Testing fields
  inTestingScope: boolean;
  testPlan: string;
  walkthroughResult: string;
  isDesignEffective: string;
  samplesTotal: number;
  samplesTested: number;
  interimCompleted: string;
  finalCompleted: string;
  operatingEffectiveness: string;
  auditorId: string | null;
  budgetHours: number;

  // Metadata
  selectedCycle: CycleItem;
  selectedProcess: ProcessItem;
  attachments: FileSelect[];
  documentIds: string[];
  objectiveIds: string[];
  applicationIds: string[];
}

interface FileSelect {
  fileId?: string;
  name: string;
  file?: File;
}

// Interface for selected objectives with display title
interface SelectedObjective {
  id: string;
  processCode: string; // e.g., "A01"
  objectiveNumber: string; // e.g., "01"
  riskRanking: string;
  assertions: string[];
}

export interface ControlActivityItemPageProps {
  mode: 'add' | 'edit';
  initialValues?: Partial<ControlActivityFormData> & {
    id?: string;
    controlCode?: string;
    riskAssessmentId?: string;
    status?: 'Draft' | 'Active';
  };
  onSubmit: (formData: ControlActivityFormData, isDraft: boolean) => Promise<void>;
  annualReportId: string;
}

const { Title } = Typography;

// ==================== STYLED COMPONENTS ====================

const PageContainer = styled.div`
   padding: 0;
`;

const PageHeader = styled.div`
  margin-bottom: 24px;
  gap: 24px !important;
`;

const PageTitle = styled(Title)`
  margin: 0 0 24px 0 !important;
  font-family: Inter !important;
  font-weight: 500 !important;
  font-size: 18px !important;
  line-height: 100% !important;
  letter-spacing: 0.005em !important;
  color: #1F2937 !important;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  padding-top: 200px;
  z-index: 1000;
  border-radius: 6px;
`;

const LoadingSpinner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;

  .spinner {
    width: 48px;
    height: 48px;
    border: 4px solid #E5E7EB;
    border-top-color: #2563EB;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  .text {
    font-family: Inter;
    font-weight: 500;
    font-size: 14px;
    color: #6B7280;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const HeaderTop = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 32px;
  margin-bottom: 16px;
`;

// Selectors Section (like RiskAssessmentDetailPage)
const SelectorsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 32px;
`;

const SelectorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const CycleSelector = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    opacity: 0.7;
  }
`;

const CycleText = styled.span`
  font-family: Inter;
  font-weight: 600;
  font-size: 20px;
  line-height: 100%;
  letter-spacing: 0.005em;
  color: #1F2937;
`;

const ChevronIcon = styled.div<{ $color?: string }>`
  display: flex;
  align-items: center;
  color: ${props => props.$color || '#6B7280'};
  
  svg {
    width: 20px;
    height: 20px;
  }
  
  svg path {
    stroke: ${props => props.$color || '#6B7280'};
  }
`;

const ProcessSelectorsWrapper = styled.div<{ $disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  cursor: ${props => props.$disabled ? 'default' : 'pointer'};
  opacity: ${props => props.$disabled ? 0.7 : 1};
`;

const ProcessChipDisplay = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  height: 28px;
  background: #EFF6FF;
  color: #1E40AF;
  border-radius: 12px;
  font-family: Inter;
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.005em;
`;

// Dropdown styles
const DropdownMenu = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.1);
  padding: 8px;
  width: 346px;
  max-height: 400px;
  overflow-y: auto;
`;

const SearchInput = styled(Input)`
  margin-bottom: 8px;
  border-radius: 6px;
  height: 38px;
  
  .ant-input {
    font-family: Inter;
    font-size: 15px;
  }
`;

const DropdownSection = styled.div`
  margin-bottom: 8px;
`;

const DropdownSectionLabel = styled.div`
  font-family: Inter;
  font-weight: 500;
  font-size: 12px;
  color: #6B7280;
  padding: 8px 12px 8px 12px;
`;

const CycleDropdownItem = styled.div<{ $selected?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s;
  background: ${props => props.$selected ? '#EFF6FF' : 'white'};

  &:hover {
    background: #F3F4F6;
  }
`;

const CycleDropdownItemText = styled.span`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  color: #1F2937;
  flex: 1;
`;

const CycleDropdownItemIcon = styled.div`
  display: flex;
  align-items: center;
  color: #6B7280;
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const ProcessDropdownItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  cursor: pointer;
  margin-bottom: 8px;
  padding: 4px 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ProcessDropdownItemChip = styled.div<{ $selected?: boolean }>`
  display: flex;
  align-items: center;
  padding: 6px 12px;
  height: 28px;
  border-radius: 50px;
  background: ${props => props.$selected ? '#2563EB' : '#EFF6FF'};
  transition: all 0.3s;

  &:hover {
    opacity: 0.9;
  }
`;

const ProcessDropdownItemText = styled.span<{ $selected?: boolean }>`
  font-family: Inter;
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.005em;
  color: ${props => props.$selected ? '#FFFFFF' : '#1E40AF'};
  flex: 1;
`;

// Empty state for selecting cycle/process
const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  gap: 16px;
`;

const EmptyStateText = styled.div`
  font-family: Inter;
  font-size: 16px;
  color: #6B7280;
  text-align: center;
`;

const EmptyStateSubtext = styled.div`
  font-family: Inter;
  font-size: 14px;
  color: #9CA3AF;
  text-align: center;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: #F3F4F6;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  color: #6B7280;

  &:hover {
    background: #E5E7EB;
    color: #1F2937;
  }

  .anticon {
    font-size: 16px;
  }
`;

const PageTitleSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 1;
`;

const PageTitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ControlId = styled.h2`
  margin: 0;
  font-family: Inter;
  font-weight: 600;
  font-size: 24px;
  color: #1F2937;
`;

const ProcessOwnerChip = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #EFF6FF;
  border-radius: 50px;
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  color: #1E40AF;
`;

const ProcessOwnerAvatar = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #3B82F6;
  color: #FFFFFF;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
`;

const ContentWrapper = styled.div`
  flex: 1;
  overflow-y: auto;
  padding-bottom: 100px; // Space for fixed footer
`;

const StyledTabs = styled(Tabs)`
  .ant-tabs-nav {
    margin-bottom: 24px;
  }

  .ant-tabs-tab {
    padding: 12px 16px;
    
    .ant-tabs-tab-btn {
      font-family: Inter;
      font-weight: 500;
      font-size: 14px;
      color: #6B7280;
    }

    svg path,
    svg circle {
      stroke: #6B7280;
      transition: stroke 0.3s;
    }

    &.ant-tabs-tab-active {
      .ant-tabs-tab-btn {
        color: #2563EB;
      }

      svg path,
      svg circle {
        stroke: #2563EB;
      }
    }

    &:hover {
      .ant-tabs-tab-btn {
        color: #2563EB;
      }

      svg path,
      svg circle {
        stroke: #2563EB;
      }
    }
  }

  .ant-tabs-ink-bar {
    background: #2563EB;
  }
`;

const TabLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    width: 20px;
    height: 20px;
  }
`;

const TabContent = styled.div`
  padding: 8px 0;
`;

const Section = styled.div`
  margin-bottom: 16px;
`;

// Frame for the 6 Ws control description components
const ControlDescriptionFrame = styled.div`
  border: 2px solid #E0E7FF;
  border-radius: 8px;
  padding: 0 16px;
  margin-bottom: 16px;
`;

const SectionTitle = styled.h3`
  font-family: Inter;
  font-weight: 600;
  font-size: 16px;
  color: #1F2937;
  margin: 0 0 16px 0;
`;

const IdentitySection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
`;

const IdentityRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const IdentityLabel = styled.span`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  color: #6B7280;
  min-width: 80px;
`;

const CycleChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  border-radius: 50px;
  font-size: 14px;
  font-weight: 500;
  background: #ECFDF5;
  color: #047857;
`;

const ProcessChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  border-radius: 50px;
  font-size: 14px;
  font-weight: 500;
  background: #EFF6FF;
  color: #1E40AF;
`;

const ControlIdChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  border-radius: 50px;
  font-size: 14px;
  font-weight: 500;
  background: #FEF3C7;
  color: #92400E;
`;

const FieldRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 16px 0;
  border-bottom: 1px solid #E5E7EB;
  
  &:last-child {
    border-bottom: none;
  }
`;

const FieldLabel = styled.label<{ $isPlaceholder?: boolean; $isDisabled?: boolean }>`
  font-family: Inter;
  font-weight: 600;
  font-size: 14px;
  min-width: 188px;
  max-width: 188px;
  color: ${props => props.$isPlaceholder ? '#EF4444' : props.$isDisabled ? '#9CA3AF' : '#1F2937'};
  display: flex;
  align-items: center;
  gap: 4px;
  padding-top: 4px;

  .info-icon {
    color: #9CA3AF;
    font-size: 14px;
    cursor: help;
    margin-left: 2px;

    &:hover {
      color: #6B7280;
    }
  }
`;

const RequiredMark = styled.span`
  color: #DC2626;
`;

const ReadOnlyBadge = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: #9CA3AF;
  background: #F3F4F6;
  padding: 2px 6px;
  border-radius: 4px;
`;

const StyledInput = styled(Input)`
  flex: 1;
  border: none;
  border-radius: 0;
  padding: 0;
  font-family: Inter;
  font-weight: 400;
  font-size: 14px;
  color: #1F2937;
  box-shadow: none !important;
  
  &:focus, &:hover {
    border: none;
    box-shadow: none;
  }
  
  &:disabled {
    background: transparent;
    color: #6B7280;
    cursor: default;
  }
  
  &::placeholder {
    color: #9CA3AF;
  }
`;

const StyledTextArea = styled(TextArea)`
  flex: 1;
  border: none !important;
  border-radius: 0;
  padding: 0;
  font-family: Inter;
  font-weight: 400;
  font-size: 14px;
  color: #1F2937;
  box-shadow: none !important;
  
  &:focus, &:hover {
    border: none !important;
    box-shadow: none !important;
  }
  
  &:disabled {
    background: transparent;
    color: #6B7280;
    cursor: default;
  }
  
  &::placeholder {
    color: #9CA3AF;
  }

  textarea {
    resize: vertical !important;
  }

  textarea::-webkit-resizer {
    background: linear-gradient(135deg, transparent 40%, #9CA3AF 40%, #9CA3AF 45%, transparent 45%, transparent 60%, #9CA3AF 60%, #9CA3AF 65%, transparent 65%, transparent 80%, #9CA3AF 80%, #9CA3AF 85%, transparent 85%);
    cursor: ns-resize;
    width: 16px;
    height: 16px;
  }
`;

const StyledSelect = styled(Select)`
  flex: 1;
  margin-top: -8px;
  
  .ant-select-selector {
    border: none !important;
    border-radius: 0 !important;
    padding: 0 !important;
    font-family: Inter;
    font-weight: 400;
    font-size: 14px;
    box-shadow: none !important;
    background: transparent !important;
    min-height: auto !important;
    height: auto !important;
    
    .ant-select-selection-item {
      line-height: 1.5 !important;
      padding: 0 !important;
    }
    
    .ant-select-selection-placeholder {
      line-height: 1.5 !important;
      color: #9CA3AF;
    }
    
    .ant-select-selection-search {
      inset-inline-start: 0 !important;
    }
  }
  
  &.ant-select-disabled .ant-select-selector {
    background: transparent !important;
    color: #6B7280 !important;
    cursor: default;
  }
`;

const SwitchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
`;

const TestingScopeChip = styled.span<{ $inScope: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  border-radius: 50px;
  font-size: 14px;
  font-weight: 500;
  background: ${props => props.$inScope ? '#ECFDF5' : '#FEF2F2'};
  color: ${props => props.$inScope ? '#047857' : '#DC2626'};
`;

// Custom Tag Component for Application Select
const ApplicationSelectTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  background: #F3F4F6;
  border: none;
  border-radius: 50px;
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  line-height: 1;
  color: #374151;
  margin: 2px;
`;

const ApplicationCloseIcon = styled(CloseCircleIcon)<{ $circleFill: string; $strokeColor: string }>`
  cursor: pointer;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  path:first-child {
    fill: ${props => props.$circleFill};
  }
  
  path:last-child {
    stroke: ${props => props.$strokeColor};
  }
  
  &:hover {
    opacity: 0.8;
  }
`;

const AttachmentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
`;

const StyledUpload = styled(Upload)`
  width: 100%;
  .ant-btn {
    width: 100%;
    height: 44px;
    border-radius: 8px;
    font-family: Inter;
    font-weight: 500;
    font-size: 14px;
    color: #2563EB;
    border-color: #2563EB;

    &:hover {
      color: #1D4ED8 !important;
      border-color: #1D4ED8 !important;
    }

    &:disabled {
      color: #9CA3AF !important;
      border-color: #D1D5DB !important;
    }
  }
`;

const FileListWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const FileItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #EFF6FF;
  color: #1E40AF;
  border-radius: 50px;
  font-size: 13px;
  font-weight: 500;
  
  .file-name {
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const StyledCloseIcon = styled(CloseCircleIcon) <{ $circleFill: string; $strokeColor: string }>`
  cursor: pointer;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 0.7;
  }
  
  circle {
    fill: ${props => props.$circleFill};
  }
  
  path {
    stroke: ${props => props.$strokeColor};
  }
`;

const ChipContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  flex: 1;
`;

const AssertionChip = styled.span`
  display: inline-flex;
  align-items: center;
  margin: 2px;
  padding: 6px 12px;
  background: #FEF3C7;
  color: #92400E;
  border-radius: 50px;
  font-size: 13px;
  font-weight: 500;
`;

// Objectives Search Mode Styled Components
const ObjectivesSearchContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  height: 100%;
  overflow-y: auto;
  padding-bottom: 100px;
  margin-top: -16px;
`;

const ObjectivesSearchHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

// Grid wrapper for objectives search
const ObjectivesGridWrapper = styled.div`
  background: #E0E7FF;
  border-radius: 6px;
  border: 1px solid #E0E7FF;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);  
  overflow: hidden;
  padding: 0 0 0px 2px;

  .e-grid {
    border: none;
  }

  .e-grid .e-headercell {
    background: #F8FAFC;
    font-family: Inter;
    font-weight: 500;
    font-size: 10px;
    color: #6B7280;
    padding: 6px 8px;
    border-color: #E5E7EB;
    border-right: 1px solid #E5E7EB;
  }

  .e-grid .e-headercell:last-child {
    border-right: none;
  }

  .e-grid .e-rowcell {
    border-color: #E5E7EB;
    border-right: 1px solid #E5E7EB;
    padding: 6px 8px;
    font-size: 12px;
    color: #1F2937;
    white-space: normal !important;
    overflow: visible !important;
    text-overflow: unset !important;
  }

  .e-grid .e-rowcell:last-child {
    border-right: none;
  }

  .e-grid .e-emptyrow {
    display: none;
  }

  /* Prevent dark hover/active state */
  .e-grid .e-row.e-active .e-rowcell,
  .e-grid .e-row.e-selectionbackground .e-rowcell,
  .e-grid .e-row.e-active,
  .e-grid .e-row.e-selectionbackground {
    background: transparent !important;
  }
`;

const IndexCell = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  padding: 0 12px;
  border: 1px solid #E5E7EB;
  border-radius: 14px;
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  color: #6B7280;
`;

const CellContent = styled.div`
  font-family: Inter;
  font-size: 13px;
  color: #1F2937;
  line-height: 1.5;
  
  p {
    margin: 0;
  }
`;

const CheckboxCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  
  .ant-checkbox-wrapper {
    .ant-checkbox-inner {
      width: 20px;
      height: 20px;
      border-radius: 4px;
    }
    
    .ant-checkbox-checked .ant-checkbox-inner {
      background-color: #2563EB;
      border-color: #2563EB;
    }
  }
`;

const SelectedObjectiveTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: #EFF6FF;
  color: #1E40AF;
  border-radius: 50px;
  font-size: 13px;
  font-weight: 500;
  margin: 2px;
`;

const ObjectiveProcessCode = styled.span`
  font-weight: 600;
  margin-right: 4px;
`;

const ObjectiveNumbersGroup = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const ObjectiveNumberCircle = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  padding: 0 6px;
  background: transparent;
  color: #2563EB;
  border: 1px solid #D1D5DB;
  border-radius: 50%;
  font-size: 11px;
  font-weight: 600;
`;

const ObjectivesFieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  flex: 1;
  cursor: pointer;
  min-height: 32px;
`;

const ObjectivesExpandIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6B7280;
  cursor: pointer;
  margin-right: 10px;
  
  &:hover {
    color: #2563EB;
  }
`;

// Chip for Financial Statement Assertions
const RiskChip = styled.span<{ $risk: string }>`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 50px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
  background: white;

  ${props => {
    switch (props.$risk) {
      case 'Med':
        return `
          border: 1px solid #FACC15;
          color: #FACC15;
        `;
      case 'Low':
        return `
          border: 1px solid #2DD4BF;
          color: #2DD4BF;
        `;
      case 'High':
        return `
          border: 1px solid #EF4444;
          color: #EF4444;
        `;
      case 'Unranked':
      default:
        return `
          border: 1px solid #60A5FA;
          color: #60A5FA;
        `;
    }
  }}

  &:hover {
    opacity: 0.8;
  }

  &::before {
    content: '●';
    margin-right: 6px;
    font-size: 10px;
  }
`;

// Tooltip components for multiple assertions
const TooltipTitle = styled.div`
  font-family: Inter;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  color: #1F2937;
  text-align: center;
  margin-bottom: 12px;
`;

const TooltipList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TooltipItem = styled.div`
  padding: 8px 12px;
  background: #FEF9C3;
  border-radius: 6px;
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #854D0E;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    background: #FDE68A;
    transform: translateX(2px);
  }
`;

const Footer = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px 32px;
  background: #FFFFFF;
  border-top: 1px solid #E5E7EB;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  z-index: 100;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
`;

const StyledOutlinedButton = styled(Button)`
  height: 36px;
  padding: 0 24px;
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  border-radius: 6px;
  border: 1px solid #2563EB;
  color: #2563EB;

  &:disabled {
    color: #9CA3AF !important;
    border-color: #D1D5DB !important;
  }

  &:hover {
    border-color: #1D4ED8 !important;
    color: #1D4ED8 !important;
  }
`;

const StyledFilledButton = styled(Button)`
  height: 36px;
  padding: 0 24px;
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  background: #2563EB;
  border-color: #2563EB;
  color: #FFFFFF;
  
  &:hover {
    background: #1D4ED8 !important;
    border-color: #1D4ED8 !important;
    color: #FFFFFF !important;
  }

  &:disabled {
    color: #9CA3AF !important;
    border-color: #D1D5DB !important;
  }
`;

const FileLink = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: #EFF6FF;
  color: #1E40AF;
  border-radius: 50px;
  font-size: 12px;
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s;
  white-space: nowrap;

  &:hover {
    opacity: 0.8;
  }

  svg {
    width: 12px;
    height: 12px;
    flex-shrink: 0;
  }
`;

const FileNoLink = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: #EFF6FF;
  color: #1E40AF;
  border-radius: 50px;
  font-size: 12px;
  font-weight: 500;
  text-decoration: none;
  transition: all 0.3s;
  white-space: nowrap;

  svg {
    width: 12px;
    height: 12px;
    flex-shrink: 0;
  }
`;

// ==================== CONSTANTS ====================

const frequencyOptions = [
  { value: 'Multiple times per day', label: 'Multiple times per day' },
  { value: 'Daily', label: 'Daily' },
  { value: 'Weekly', label: 'Weekly' },
  { value: 'Monthly', label: 'Monthly' },
  { value: 'Quarterly', label: 'Quarterly' },
  { value: 'Semi-Annual', label: 'Semi-Annual' },
  { value: 'Annual', label: 'Annual' },
  { value: 'Ongoing', label: 'On-going' }
];

const categoryOptions = [
  { value: 'financial', label: 'Financial' },
  { value: 'operational', label: 'Operational' },
  { value: 'regulatory', label: 'Regulatory' },
];

const controlTypeOptions = [
  { value: 'preventative', label: 'Preventative' },
  { value: 'detective', label: 'Detective' },
  { value: 'other', label: 'Other' },
];

const effectivenessOptions = [
  { value: 'notStarted', label: 'WIP' },
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

const completionOptions = [
  { value: 'notStarted', label: 'Not Started' },
  { value: 'wip', label: 'WIP' },
  { value: 'yes', label: 'Yes' },
];

// Final Status values (read-only, auto-calculated)
const FINAL_STATUS = {
  NOT_STARTED: 'Not Started',
  WIP: 'WIP',
  COMPLETED: 'Completed',
} as const;

// Styled components for Auditor Select
const SelectAuditorCell = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const SelectAvatar = styled.div`
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #EFF6FF;
  border-radius: 50%;
  font-family: Inter;
  font-weight: 600;
  font-size: 11px;
  color: #1E40AF;
  flex-shrink: 0;
`;

const SelectName = styled.span`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  color: #1F2937;
`;

// Helper: Get user initials from name
const getUserInitials = (name: string): string => {
  if (!name) return '?';
  const parts = name.trim().split(' ').filter(Boolean);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

// ==================== MAIN COMPONENT ====================

const ControlActivityItemPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { controlId } = useParams<{ controlId: string }>();

  // Redux state
  const { selectedReport } = useSelector((state: RootState) => state.annualReport);
  const { controlActivities, lastFetchedReportId, auditors, auditorsLastFetchedCompanyId } = useSelector(
    (state: RootState) => state.controlActivities
  );
  const { isAdmin } = useSelector((state: RootState) => state.auth);
  const { applications, applicationsLastFetchedReportId } = useSelector(
    (state: RootState) => state.auditPlan
  );

  // Determine mode and find control data
  const mode: 'add' | 'edit' = controlId && controlId !== 'create' ? 'edit' : 'add';
  const controlData = mode === 'edit' ? controlActivities.find(c => c.id === controlId) : null;

  // Local state for loading
  const [isLoadingControl, setIsLoadingControl] = useState(false);
  const [controlNotFound, setControlNotFound] = useState(false);

  // Local state
  const [isSearching, setIsSearching] = useState(false);
  const [cycleResults, setCycleResults] = useState<CycleItem[]>([]);
  const [processResults, setProcessResults] = useState<ProcessItem[]>([]);
  const [selectedCycle, setSelectedCycle] = useState<CycleItem | null>(null);
  const [selectedProcess, setSelectedProcess] = useState<ProcessItem | null>(null);
  const [generatedControlCode, setGeneratedControlCode] = useState<string>('');
  const [isSavingAsDraft, setIsSavingAsDraft] = useState(false);
  const [isSavingAsActive, setIsSavingAsActive] = useState(false);
  const [attachments, setAttachments] = useState<FileSelect[]>([]);
  
  // GAP auto-creation prompt (post-save: asks user to view the created GAP)
  const [gapPromptOpen, setGapPromptOpen] = useState(false);
  const [createdGapId, setCreatedGapId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('1');

  // GAP pre-save confirmation (asks user whether to auto-create a GAP)
  const [gapAutoCreateModalOpen, setGapAutoCreateModalOpen] = useState(false);
  const [pendingSaveType, setPendingSaveType] = useState<'draft' | 'active' | null>(null);

  // Search terms for dropdowns
  const [cycleSearchTerm, setCycleSearchTerm] = useState('');
  const [processSearchTerm, setProcessSearchTerm] = useState('');

  // Dropdown open states
  const [cycleDropdownOpen, setCycleDropdownOpen] = useState(false);
  const [processDropdownOpen, setProcessDropdownOpen] = useState(false);

  // Objectives search mode states
  const [objectivesSearchMode, setObjectivesSearchMode] = useState(false);
  const [objSearchCycle, setObjSearchCycle] = useState<CycleItem | null>(null);
  const [objSearchProcess, setObjSearchProcess] = useState<ProcessItem | null>(null);
  const [isSearchingCycles, setIsSearchingCycles] = useState(false);
  const [isSearchingProcesses, setIsSearchingProcesses] = useState(false);
  const [objSearchCycleResults, setObjSearchCycleResults] = useState<CycleItem[]>([]);
  const [objSearchProcessResults, setObjSearchProcessResults] = useState<ProcessItem[]>([]);
  const [objSearchObjectives, setObjSearchObjectives] = useState<any[]>([]);
  const [objSearchLoading, setObjSearchLoading] = useState(false);
  const [tempSelectedObjectives, setTempSelectedObjectives] = useState<SelectedObjective[]>([]);
  const [selectedObjectives, setSelectedObjectives] = useState<SelectedObjective[]>([]);
  const [objCycleSearchTerm, setObjCycleSearchTerm] = useState('');
  const [objProcessSearchTerm, setObjProcessSearchTerm] = useState('');
  const [objCycleDropdownOpen, setObjCycleDropdownOpen] = useState(false);
  const [objProcessDropdownOpen, setObjProcessDropdownOpen] = useState(false);
  const [riskRank, setRiskRank] = useState<string>('Unranked');
  const [assertions, setAssertions] = useState<string[]>([]);

  // Applications state
  const [selectedApplicationIds, setSelectedApplicationIds] = useState<string[]>([]);
  const [isLoadingApplications, setIsLoadingApplications] = useState(false);

  // For checking effectivenessGapId for GAP auto-creation prompt
  const [currentEffectivenessGapId, setCurrentEffectivenessGapId] = useState<string | null>(null);

  // Form instance
  const [form] = Form.useForm();

  // Watch inTestingScope for conditional rendering
  const inTestingScope = Form.useWatch('inTestingScope', form);
  const formWhen = Form.useWatch('when', form);

  // Watch controlCode for display
  const watchedControlCode = Form.useWatch('controlCode', form);
  const displayControlCode = watchedControlCode || generatedControlCode || controlData?.controlCode || '';

  // Reset local state
  const resetLocalState = () => {
    setSelectedCycle(null);
    setSelectedProcess(null);
    setGeneratedControlCode('');
    setAttachments([]);
    setSelectedObjectives([]);
    setSelectedApplicationIds([]);
    form.resetFields();
  };

  // Ref to prevent concurrent fetches (fixes double API call issue)
  const isFetchingControlsRef = useRef(false);

  // Load control activities list if not already loaded
  useEffect(() => {
    const loadControlActivitiesList = async () => {
      if (!selectedReport?.annualReportId) return;

      // Prevent concurrent fetches
      if (isFetchingControlsRef.current) {
        console.warn('⏳ Already fetching controls, skipping...');
        return;
      }

      // Check if we already have cached data for this report
      if (lastFetchedReportId === selectedReport.annualReportId) {
        console.warn('✅ Using cached control activities data');
        return;
      }

      isFetchingControlsRef.current = true;
      setIsLoadingControl(true);
      try {
        const response = await getControlActivities({ annualReportId: selectedReport.annualReportId });
        debugger;
        dispatch(setControlActivities({
          data: response.data,
          totalCount: response.totalCount,
          reportId: selectedReport.annualReportId
        }));
      } catch (error) {
        console.error('Failed to load control activities:', error);
        message.error('Failed to load control activities');
      } finally {
        setIsLoadingControl(false);
        isFetchingControlsRef.current = false;
      }
    };

    loadControlActivitiesList();
  }, [selectedReport?.annualReportId, lastFetchedReportId, dispatch]);

  useEffect(() => {
    if (mode === 'add') {
      setTimeout(() => {
        setCycleDropdownOpen(true);
      }, 200);
    }
  }, [mode]);

  // Check if control exists in edit mode
  useEffect(() => {
    if (mode === 'edit' && !isLoadingControl) {
      if (controlActivities.length > 0 && !controlData) {
        // List is loaded but control not found
        setControlNotFound(true);
      } else {
        setControlNotFound(false);
      }
    }
  }, [mode, controlData, controlActivities.length, isLoadingControl]);

  // Initialize form when control data is available (edit mode)
  useEffect(() => {
    if (mode === 'edit' && controlData) {
      // Set cycle and process
      if (controlData.cycle) {
        setSelectedCycle({
          cycleId: controlData.cycle.id,
          cycleCode: controlData.cycle.code,
          cycleName: controlData.cycle.name,
        });
      }

      if (controlData.process) {
        setSelectedProcess({
          processId: controlData.process.id,
          processCode: controlData.process.code,
          processName: controlData.process.name,
          processOwnerId: controlData.processOwner?.id || '',
          processOwnerName: controlData.processOwner?.name || '',
          riskAssessmentId: controlData.riskAssessmentId || '',
        });
      }

      // Set attachments if available
      if (controlData.testing?.attachments) {
        setAttachments(controlData.testing.attachments.map(att => ({
          fileId: att.id,
          name: att.name,
        })));
      }

      // Set selected objectives from objectiveIds, objectiveNumbers, and objectiveProcessCodes
      if (controlData.objectiveIds && controlData.objectiveNumbers) {
        // Parse the new format: ObjectiveNumbers = "01~02", ObjectiveProcessCodes = "A01~A01"
        const objectiveNumbers = controlData.objectiveNumbers.split('~');
        const objectiveProcessCodes = controlData.objectiveProcessCodes?.split('~') || [];
        
        const selectedObjs: SelectedObjective[] = controlData.objectiveIds.map((id, index) => ({
          id,
          processCode: objectiveProcessCodes[index] || '',
          objectiveNumber: objectiveNumbers[index] || '',
          riskRanking: controlData.riskRanking || 'Unranked',
          assertions: controlData.assertions || [],
        }));
        setSelectedObjectives(selectedObjs);
      }

      // Set risk rank and assertions
      setRiskRank(controlData.riskRanking || 'Unranked');
      setAssertions(controlData.assertions || []);

      setCurrentEffectivenessGapId(controlData.effectivenessGapId || null);

      // Populate form fields
      form.setFieldsValue({
        controlCode: controlData.controlCode || '',
        what: controlData.controlDescription?.what || '',
        who: controlData.controlDescription?.who || '',
        why: controlData.controlDescription?.why || '',
        how: controlData.controlDescription?.how || '',
        when: controlData.controlDescription?.when || '',
        evidence: controlData.controlDescription?.evidence || '',
        category: controlData.controlCategory || '',
        controlType: controlData.controlType || '',
        isKeyControl: controlData.keyOrNonKey === 'Key',
        controlOwner: controlData.controlOwner || '',
        objectiveIds: controlData.objectiveIds || [],
        isGapCompensating: controlData.isGapCompensating ?? false,
        inTestingScope: controlData.testingScope ?? false,
        testPlan: controlData.testing?.testPlan || '',
        walkthroughResult: controlData.testing?.walkthroughResult || '',
        isDesignEffective: controlData.testing?.designEffective || 'Not Started',
        operatingEffectiveness: controlData.testing?.operatingEffectiveness || 'Not Started',
        samplesTotal: controlData.testing?.totalTestSamples || 0,
        samplesTested: controlData.testing?.samplesTested || 0,
        interimCompleted: controlData.testing?.interimCompleted || 'Not Started',
        auditorId: controlData.testing?.auditorAssigned?.id || null,
        budgetHours: controlData.testing?.budgetHoursToTest || 0,
        controlWeight: controlData.controlWeight && controlData.controlWeight >= 0 ? controlData.controlWeight : undefined,
      });
    } else if (mode === 'add') {
      // Add mode - start fresh with cycle selection
      resetLocalState();
    }
  }, [mode, controlData, form]);

  // Initialize selected applications in edit mode
  useEffect(() => {
    if (mode === 'edit' && controlData?.applications) {
      setSelectedApplicationIds(controlData.applications.map(app => app.id));
    }
  }, [mode, controlData]);

  // Fetch applications on page load if not cached
  useEffect(() => {
    const fetchApplicationsData = async () => {
      if (!selectedReport?.annualReportId) return;

      // Check if we already have cached data for this report
      if (applicationsLastFetchedReportId === selectedReport.annualReportId && applications.length > 0) {
        // console.log('✅ Using cached applications data');
        return;
      }

      setIsLoadingApplications(true);
      try {
        const response = await getApplications({
          annualReportId: selectedReport.annualReportId,
        });
        dispatch(setApplications({
          data: response.data,
          totalCount: response.totalCount,
          reportId: selectedReport.annualReportId,
        }));
      } catch (error) {
        console.error('Failed to load applications:', error);
      } finally {
        setIsLoadingApplications(false);
      }
    };

    fetchApplicationsData();
  }, [selectedReport?.annualReportId, applicationsLastFetchedReportId, applications.length, dispatch]);

  // Fetch auditors on page load if not cached (offline-first)
  useEffect(() => {
    const fetchAuditorsData = async () => {
      const companyId = localStorage.getItem("companyId");

      if (!companyId) return;

      // Check if we already have cached data for this company
      if (auditorsLastFetchedCompanyId === companyId && auditors.length > 0) {
        return;
      }

      try {
        const response = await getAuditors({
          companyId: companyId,
        });
        dispatch(setAuditors({
          auditors: response,
          companyId: companyId,
        }));
      } catch (error) {
        console.error('Failed to load auditors:', error);
      }
    };

    fetchAuditorsData();
  }, [auditorsLastFetchedCompanyId, auditors.length, dispatch]);

  // Search cycles for dropdown
  useEffect(() => {
    if (!selectedReport?.annualReportId || mode === 'edit') return;

    const fetchCyclesData = async () => {
      setIsSearching(true);
      try {
        const results = await searchCycles({
          annualReportId: selectedReport.annualReportId,
          searchTerm: cycleSearchTerm,
        });
        setCycleResults(results);
      } catch (error) {
        console.error('Failed to search cycles:', error);
        setCycleResults([]);
      } finally {
        setIsSearching(false);
      }
    };

    const timeoutId = setTimeout(fetchCyclesData, 300);
    return () => clearTimeout(timeoutId);
  }, [cycleSearchTerm, selectedReport?.annualReportId, mode]);

  // Search processes for dropdown when cycle is selected
  useEffect(() => {
    if (!selectedCycle || !selectedReport?.annualReportId || mode === 'edit') return;

    const fetchProcessesData = async () => {
      setIsSearching(true);
      try {
        const results = await searchProcesses({
          annualReportId: selectedReport.annualReportId,
          searchTerm: processSearchTerm,
          cycleIds: [selectedCycle.cycleId],
        });
        setProcessResults(results);
      } catch (error) {
        console.error('Failed to search processes:', error);
        setProcessResults([]);
      } finally {
        setIsSearching(false);
      }
    };

    const timeoutId = setTimeout(fetchProcessesData, 300);
    return () => clearTimeout(timeoutId);
  }, [processSearchTerm, selectedCycle, selectedReport?.annualReportId, mode]);

  // Search cycles for objectives search mode
  useEffect(() => {
    if (!objectivesSearchMode || !selectedReport?.annualReportId) return;

    const fetchObjCyclesData = async () => {
      setIsSearchingCycles(true);
      try {
        const results = await searchCycles({
          annualReportId: selectedReport.annualReportId,
          searchTerm: objCycleSearchTerm,
        });
        setObjSearchCycleResults(results);
      } catch (error) {
        console.error('Failed to search cycles for objectives:', error);
        setObjSearchCycleResults([]);
      } finally {
        setIsSearchingCycles(false);
      }
    };

    const timeoutId = setTimeout(fetchObjCyclesData, 300);
    return () => clearTimeout(timeoutId);
  }, [objectivesSearchMode, objCycleSearchTerm, selectedReport?.annualReportId]);

  // Search processes for objectives search mode
  useEffect(() => {
    if (!objectivesSearchMode || !objSearchCycle || !selectedReport?.annualReportId) return;

    const fetchObjProcessesData = async () => {
      setIsSearchingProcesses(true);
      try {
        const results = await searchProcesses({
          annualReportId: selectedReport.annualReportId,
          searchTerm: objProcessSearchTerm,
          cycleIds: [objSearchCycle.cycleId],
        });
        setObjSearchProcessResults(results);
      } catch (error) {
        console.error('Failed to search processes for objectives:', error);
        setObjSearchProcessResults([]);
      } finally {
        setIsSearchingProcesses(false);
      }
    };

    const timeoutId = setTimeout(fetchObjProcessesData, 300);
    return () => clearTimeout(timeoutId);
  }, [objectivesSearchMode, objProcessSearchTerm, objSearchCycle, selectedReport?.annualReportId]);

  // Generate Control ID based on process code and existing controls
  const generateControlCode = (processCode: string): string => {
    const processControls = controlActivities.filter(
      (c) => c.controlCode.startsWith(processCode)
    );
    return `${processCode}.${String(processControls.length + 1).padStart(2, '0')}`;
  };

  // ==================== OBJECTIVES SEARCH MODE FUNCTIONS ====================

  // Enter objectives search mode
  const enterObjectivesSearchMode = () => {
    setObjectivesSearchMode(true);
    // Set default to current process
    if (selectedCycle) {
      setObjSearchCycle(selectedCycle);
    }
    if (selectedProcess) {
      setObjSearchProcess(selectedProcess);
      // Fetch objectives for current process
      fetchObjectivesForSearch(selectedProcess.riskAssessmentId);
    }
    // Copy current selections to temp
    setTempSelectedObjectives([...selectedObjectives]);
  };

  // Exit objectives search mode without saving
  const exitObjectivesSearchMode = () => {
    setObjectivesSearchMode(false);
    setObjSearchCycle(null);
    setObjSearchProcess(null);
    setObjSearchObjectives([]);
    setTempSelectedObjectives([]);
    setObjCycleSearchTerm('');
    setObjProcessSearchTerm('');
  };

  // Confirm objectives selection
  const confirmObjectivesSelection = () => {
    setSelectedObjectives(tempSelectedObjectives);
    // Update form with selected objective IDs
    form.setFieldValue('objectiveIds', tempSelectedObjectives.map(o => o.id));
    calculateRiskRankAndAssertions(tempSelectedObjectives);
    exitObjectivesSearchMode();
  };

  // Calculate Risk Rank and Assertions based on selected objectives
  const calculateRiskRankAndAssertions = (newSelectedObjectives: SelectedObjective[]) => {
    // Calculate Risk Rank - max value
    const rankPriority: Record<string, number> = {
      'High': 4,
      'Med': 3,
      'Low': 2,
      'Unranked': 1,
    };

    let maxRank = 'Unranked';
    let maxPriority = 0;

    newSelectedObjectives.forEach(obj => {
      const priority = rankPriority[obj.riskRanking] || 0;
      if (priority > maxPriority) {
        maxPriority = priority;
        maxRank = obj.riskRanking;
      }
    });

    // Calculate Assertions - union of all assertions
    const assertionsSet = new Set<string>();
    newSelectedObjectives.forEach(obj => {
      obj.assertions.forEach(assertion => {
        if (assertion) assertionsSet.add(assertion);
      });
    });

    const assertions = Array.from(assertionsSet).sort();

    setRiskRank(maxRank);
    setAssertions(assertions);
  };

  // Fetch objectives for a given riskAssessmentId
  const fetchObjectivesForSearch = async (riskAssessmentId: string) => {
    if (!riskAssessmentId) return;

    setObjSearchLoading(true);
    try {
      const response = await getObjectives(riskAssessmentId, {
        skip: 0,
        take: 1000,
        requiresCounts: true,
      });
      const parsedObjectives = parseObjectives(response.result || []);
      setTempSelectedObjectives(prev => prev.map(obj => {
        const o = parsedObjectives.find((o) => o.id === obj.id);
        if (o) {
          return {
            ...obj,
            riskRanking: o.controlRiskRanking,
            assertions: o.financialStatementAssertions,
          };
        }
        return obj;
      }));
      setObjSearchObjectives(parsedObjectives);
    } catch (error) {
      console.error('Failed to fetch objectives for search:', error);
      message.error('Failed to load objectives');
      setObjSearchObjectives([]);
    } finally {
      setObjSearchLoading(false);
    }
  };

  // Handle cycle selection in objectives search
  const handleObjSearchCycleSelect = (cycle: CycleItem) => {
    setObjSearchCycle(cycle);
    setObjSearchProcess(null);
    setObjSearchObjectives([]);
    setObjCycleSearchTerm('');
    setObjCycleDropdownOpen(false);
    setObjSearchProcessResults([]);
    setTimeout(() => {
      setObjProcessDropdownOpen(true);
    }, 100);
  };

  // Handle process selection in objectives search
  const handleObjSearchProcessSelect = (process: ProcessItem) => {
    setObjSearchProcess(process);
    setObjProcessSearchTerm('');
    setObjProcessDropdownOpen(false);
    // Fetch objectives for selected process
    fetchObjectivesForSearch(process.riskAssessmentId);
  };

  // Toggle objective selection
  const toggleObjectiveSelection = (obj: any) => {
    // Extract only the process code (before any space) to remove process name if included
    const rawProcessCode = objSearchProcess?.processCode || '';
    const objectiveNumber = obj.index.toString().padStart(2, '0');
    const isSelected = tempSelectedObjectives.some(o => o.id === obj.id);

    if (isSelected) {
      setTempSelectedObjectives(prev => prev.filter(o => o.id !== obj.id));
    } else {
      setTempSelectedObjectives(prev => [...prev, { 
        id: obj.id, 
        processCode: rawProcessCode,
        objectiveNumber,
        riskRanking: obj.controlRiskRanking,
        assertions: obj.financialStatementAssertions,
      }]);
    }
  };

  // Check if objective is selected
  const isObjectiveSelected = (objId: string) => {
    return tempSelectedObjectives.some(o => o.id === objId);
  };

  // Watch form fields for validation
  const formWhat = Form.useWatch('what', form);
  const formWho = Form.useWatch('who', form);
  const formWhy = Form.useWatch('why', form);
  const formHow = Form.useWatch('how', form);
  const formEvidence = Form.useWatch('evidence', form);
  const formCategory = Form.useWatch('category', form);
  const formControlType = Form.useWatch('controlType', form);
  const formIsKeyControl = Form.useWatch('isKeyControl', form);
  const formControlOwner = Form.useWatch('controlOwner', form);
  const formTestPlan = Form.useWatch('testPlan', form);
  const formWalkthroughResult = Form.useWatch('walkthroughResult', form);
  const formIsDesignEffective = Form.useWatch('isDesignEffective', form);
  const formOperatingEffectiveness = Form.useWatch('operatingEffectiveness', form);
  const formSamplesTotal = Form.useWatch('samplesTotal', form);
  const formSamplesTested = Form.useWatch('samplesTested', form);
  const formControlWeight = Form.useWatch('controlWeight', form);

  // Calculate Testing Status percentage
  // - N/A when total = 0 or samples tested = 0 (or when both haven't been set)
  // - (x/y) % when both values are set and total > 0
  const testingStatusDisplay = useMemo(() => {
    const total = Number(formSamplesTotal) || 0;
    const tested = Number(formSamplesTested) || 0;
    
    // If Samples Total = 0, show N/A
    if (total === 0) {
      return 'N/A';
    }
    
    const percentage = (tested / total) * 100;
    return `${percentage.toFixed(2)}%`;
  }, [formSamplesTotal, formSamplesTested]);

  // Calculate Final Status based on Testing Status
  // Testing Status = Samples Tested / Samples Total
  // - "Not started" when Testing Status = 0 (Samples Tested = 0 or Samples Total = 0)
  // - "WIP" when Testing Status > 0 but < 100%
  // - "Completed" when Testing Status = 100%
  const calculatedFinalStatus = useMemo(() => {
    const total = Number(formSamplesTotal) || 0;
    const tested = Number(formSamplesTested) || 0;
    
    // If Samples Total = 0 or Samples Tested = 0, return "Not Started"
    if (total === 0 || tested === 0) {
      return FINAL_STATUS.NOT_STARTED;
    }
    
    const testingStatus = (tested / total) * 100;
    
    if (testingStatus >= 100) {
      return FINAL_STATUS.COMPLETED;
    } else {
      return FINAL_STATUS.WIP;
    }
  }, [formSamplesTotal, formSamplesTested]);

  // Custom tag render for Applications Select
  const renderApplicationTag = (props: CustomTagProps) => {
    const { label, closable, onClose } = props;
    
    const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
      event.preventDefault();
      event.stopPropagation();
    };

    return (
      <ApplicationSelectTag onMouseDown={onPreventMouseDown}>
        {label}
        {closable && (
          <ApplicationCloseIcon
            $circleFill="#E5E7EB"
            $strokeColor="#6B7280"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
          />
        )}
      </ApplicationSelectTag>
    );
  };

  // Check if form is valid for Active save
  const isValidForActive = useMemo(() => {
    // In add mode, check if cycle and process are selected
    if (mode === 'add' && (!selectedCycle || !selectedProcess)) return false;

    const requiredFields = [
      !!formWhat,
      !!formWhen,
      !!formWho,
      !!formWhy,
      !!formHow,
      !!formEvidence,
      !!formCategory,
      !!formControlType,
      formIsKeyControl !== null && formIsKeyControl !== undefined,
      !!formControlOwner,
    ];

    // If in testing scope, additional fields required
    if (inTestingScope) {
      requiredFields.push(
        formControlWeight !== null && formControlWeight !== undefined && formControlWeight >= 0,
        !!formTestPlan,
        !!formWalkthroughResult,
        formIsDesignEffective !== null && formIsDesignEffective !== undefined,
        formOperatingEffectiveness !== null && formOperatingEffectiveness !== undefined,
        formSamplesTotal !== null && formSamplesTotal !== undefined,
        formSamplesTested !== null && formSamplesTested !== undefined,
      );
    }

    return requiredFields.every(Boolean);
  }, [
    mode,
    selectedCycle,
    selectedProcess,
    formWhat, formWhen, formWho, formWhy, formHow, formEvidence,
    formCategory, formControlType, formIsKeyControl, formControlOwner,
    formControlWeight,
    inTestingScope, formTestPlan, formWalkthroughResult,
    formIsDesignEffective, formOperatingEffectiveness,
    formSamplesTotal, formSamplesTested,
  ]);

  // Check if form is valid for Draft save
  const isValidForDraft = useMemo(() => {
    if (mode === 'add') {
      // Add mode: valid once cycle and process are selected
      return !!(selectedCycle && selectedProcess);
    }
    // Edit mode: can save as draft only if status is Draft
    return controlData?.status === 'Draft';
  }, [mode, selectedCycle, selectedProcess, controlData?.status]);

  // Filter cycles for dropdown (must be before early returns)
  const filteredCycles = useMemo(() => {
    if (!cycleSearchTerm) return cycleResults;
    const term = cycleSearchTerm.toLowerCase();
    return cycleResults.filter(cycle =>
      cycle.cycleCode.toLowerCase().includes(term) ||
      cycle.cycleName.toLowerCase().includes(term)
    );
  }, [cycleSearchTerm, cycleResults]);

  // Filter processes for dropdown (must be before early returns)
  const filteredProcesses = useMemo(() => {
    if (!processSearchTerm) return processResults;
    const term = processSearchTerm.toLowerCase();
    return processResults.filter(process =>
      process.processCode.toLowerCase().includes(term) ||
      process.processName.toLowerCase().includes(term)
    );
  }, [processSearchTerm, processResults]);

  // Determine if we should show the form or empty state (must be before early returns)
  const showForm = mode === 'edit' || (selectedCycle && selectedProcess && generatedControlCode);

  // Handle attachment selection
  const handleAttachmentSelect = (file: File): boolean => {
    const isDuplicate = attachments.some(att =>
      att.name === file.name && att.file?.size === file.size
    );

    if (isDuplicate) {
      message.warning(`File "${file.name}" has already been selected`);
      return false;
    }

    setAttachments(prev => [...prev, {
      file: file,
      name: file.name,
    }]);
    return false; // Prevent default upload behavior
  };

  // Handle attachment delete
  const handleAttachmentDelete = (fileSelect: FileSelect) => {
    setAttachments(prev => prev.filter(f => f !== fileSelect));
  };

  const [isDownloading, setIsDownloading] = useState(false);
    
  const handleDownload = async (e: React.MouseEvent, fileSelect: FileSelect) => {
      e.stopPropagation();
      e.preventDefault();
      if (isDownloading) {
          return;
      }

      setIsDownloading(true);
      try {
          let fileId = fileSelect.fileId ?? "";
          let fileName = fileSelect.name;

          if (fileSelect.fileId)
              await downloadFile(fileId, fileName);
      } catch (error) {
          console.error('Failed to download file:', error);
          message.error('Failed to download file');
      } finally {
          setIsDownloading(false);
      }
  };

  // Prepare form data for submission
  const buildBaseRequest = async (): Promise<CreateControlRequest> => {
    if (!selectedProcess || !selectedCycle) {
      throw new Error('Cycle and Process must be selected');
    }

    // Upload new attachments first
    const uploadedDocIds: string[] = [];
    const newAttachments = attachments.filter(att => !att.fileId && att.file);

    if (newAttachments.length > 0) {
      for (const attachment of newAttachments) {
        if (attachment.file) {
          try {
            const uploadResponse = await uploadControlTestSheet(attachment.file);
            uploadedDocIds.push(uploadResponse.data.id);
          } catch (uploadError) {
            console.error(`Failed to upload ${attachment.name}:`, uploadError);
            message.error(`Failed to upload ${attachment.name}`);
            throw uploadError;
          }
        }
      }
    }

    const values = form.getFieldsValue();

    // Prepare documentIds
    const documentIds: string[] = [...(values.documentIds || []), ...uploadedDocIds];

    const baseRequest = {
      weight: Number(values.controlWeight ?? 0),
      what: values.what || '',
      when: values.when || 'Daily',
      who: values.who || '',
      why: values.why || '',
      how: values.how || '',
      evidence: values.evidence || '',
      category: (values.category || 'financial') as 'financial' | 'operational' | 'regulatory',
      controlType: (values.controlType || 'preventative') as 'preventative' | 'detective' | 'other',
      controlFrequency: frequencyToApiEnum(values.when),
      isKeyControl: values.isKeyControl || false,
      controlOwner: values.controlOwner || '',
      isGapCompensating: values.isGapCompensating || false,
      processId: selectedProcess.processId,
      inTestingScope: values.inTestingScope || false,
      testPlan: values.testPlan || '',
      walthroughResult: values.walkthroughResult || '',
      isDesignEffective: convertEffectiveness(values.isDesignEffective),
      samplesTotal: Number(values.samplesTotal ?? "0"),
      samplesTested: Number(values.samplesTested ?? "0"),
      documentIds: documentIds,
      objectiveIds: selectedObjectives.map(o => o.id),
      applicationIds: selectedApplicationIds,
      interimCompleted: convertInterimCompleted(values.interimCompleted),
      finalCompleted: convertFinalCompleted(calculatedFinalStatus),
      operatingEffectiveness: convertEffectiveness(values.operatingEffectiveness),
      auditorId: values.auditorId ?? null,
      budgetHours: Number(values.budgetHours ?? "0"),
    };

    return baseRequest;
  };

  // Reload control activities data after save
  const reloadData = async () => {
    if (!selectedReport?.annualReportId) return null;

    try {
      const response = await getControlActivities({ annualReportId: selectedReport.annualReportId });
      dispatch(setControlActivities({
        data: response.data,
        totalCount: response.totalCount,
        reportId: selectedReport.annualReportId
      }));
      return response.data;
    } catch (error) {
      console.error('Error refreshing control activities:', error);
      return null;
    }
  };

  /**
   * Check and show GAP auto-creation prompt if applicable
   * @param gapId - The effectivenessGapId from the response
   */
  const checkForGapPrompt = (gapId: string | null) => {
    if (gapId && gapId !== currentEffectivenessGapId) {
      setCreatedGapId(gapId);
      setGapPromptOpen(true);
    }
  };

  /**
   * Handle user confirming to view the created GAP
   */
  const handleViewGap = () => {
    if (createdGapId) {
      setGapPromptOpen(false);
      navigate(`/gaps/${createdGapId}`);
    }
  };

  /**
   * Handle user dismissing the GAP prompt
   */
  const handleDismissGapPrompt = () => {
    setGapPromptOpen(false);
    setCreatedGapId(null);
  };

  /**
   * Check if Design Effectiveness or Operating Effectiveness changed to "No"
   * compared to the original saved data. Used to decide whether to show
   * the GAP auto-creation confirmation modal before saving.
   */
  const hasEffectivenessChangedToNo = (): boolean => {
    const values = form.getFieldsValue();
    const currentDesign = (values.isDesignEffective || '').toLowerCase();
    const currentOperating = (values.operatingEffectiveness || '').toLowerCase();

    if (mode === 'add') {
      return currentDesign === 'no' || currentOperating === 'no';
    }

    const originalDesign = (controlData?.testing?.designEffective || 'Not Started').toLowerCase();
    const originalOperating = (controlData?.testing?.operatingEffectiveness || 'Not Started').toLowerCase();

    return (currentDesign === 'no' && originalDesign !== 'no') ||
           (currentOperating === 'no' && originalOperating !== 'no');
  };

  const executeSaveAsDraft = async (autoGenerateGAPs?: boolean) => {
    setIsSavingAsDraft(true);
    try {
      const baseRequest = await buildBaseRequest();
      if (autoGenerateGAPs !== undefined) {
        baseRequest.autoGenerateGAPs = autoGenerateGAPs;
      }
      const isEdit = mode === 'edit' && !!controlId;
      let response: ControlResponse;
      if (isEdit) {
        const request: UpdateControlRequest = {
          ...baseRequest,
          controlId: controlId!,
        };
        response = await updateControl(request);
        await reloadData();
        message.success('Draft saved successfully');
      } else {
        response = await createControl(baseRequest);
        await reloadData();
        message.success('Draft saved successfully');
        if (response?.data?.controlId) {
          navigate(`/control-activities/${response.data.controlId}`, { replace: true });
        }
      }
      dispatch(clearGapsData());
      dispatch(clearHierarchyData());
      checkForGapPrompt(response.data.effectivenessGapId);
    } catch (error: any) {
      console.error('Failed to save draft:', error);
      message.error(error.message || 'Failed to save draft');
    } finally {
      setIsSavingAsDraft(false);
    }
  };

  const executeSaveAsActive = async (autoGenerateGAPs?: boolean) => {
    setIsSavingAsActive(true);
    try {
      const baseRequest = await buildBaseRequest();
      if (autoGenerateGAPs !== undefined) {
        baseRequest.autoGenerateGAPs = autoGenerateGAPs;
      }
      const isEdit = mode === 'edit' && !!controlId;
      let response: ControlResponse;
      if (isEdit) {
        const request: UpdateControlRequest = {
          ...baseRequest,
          controlId: controlId!,
        };
        response = await updateControl(request);
        await reloadData();
        message.success('Control Activity saved successfully');
      } else {
        response = await createControl(baseRequest);
        await reloadData();
        message.success('Control Activity created successfully');
        if (response?.data?.controlId) {
          navigate(`/control-activities/${response.data.controlId}`, { replace: true });
        }
      }
      dispatch(clearGapsData());
      dispatch(clearHierarchyData());
      checkForGapPrompt(response.data.effectivenessGapId);
    } catch (error: any) {
      console.error('Failed to save control:', error);
      message.error(error.message || 'Failed to save control');
    } finally {
      setIsSavingAsActive(false);
    }
  };

  // Intercept Save as Draft: show GAP confirmation if effectiveness changed to "No"
  const handleSaveAsDraft = () => {
    if (hasEffectivenessChangedToNo()) {
      setPendingSaveType('draft');
      setGapAutoCreateModalOpen(true);
      return;
    }
    executeSaveAsDraft();
  };

  // Intercept Save as Active: show GAP confirmation if effectiveness changed to "No"
  const handleSaveAsActive = () => {
    if (hasEffectivenessChangedToNo()) {
      setPendingSaveType('active');
      setGapAutoCreateModalOpen(true);
      return;
    }
    executeSaveAsActive();
  };

  const handleGapAutoCreateConfirm = () => {
    setGapAutoCreateModalOpen(false);
    if (pendingSaveType === 'draft') {
      executeSaveAsDraft(true);
    } else {
      executeSaveAsActive(true);
    }
    setPendingSaveType(null);
  };

  const handleGapAutoCreateDecline = () => {
    setGapAutoCreateModalOpen(false);
    if (pendingSaveType === 'draft') {
      executeSaveAsDraft(false);
    } else {
      executeSaveAsActive(false);
    }
    setPendingSaveType(null);
  };

  // Show loading while checking for control
  if (isLoadingControl) {
    return (
      <PageContainer>
        <PageHeader>
          <PageTitle level={2}>Control Activities</PageTitle>
        </PageHeader>
        <div style={{ position: 'relative', minHeight: '400px' }}>
          <LoadingOverlay>
            <LoadingSpinner>
              <div className="spinner" />
              <div className="text">Loading control activity data...</div>
            </LoadingSpinner>
          </LoadingOverlay>
        </div>
      </PageContainer>
    );
  }

  // Navigate back to control activities with cleared processId filter
  const handleGoBack = () => {
    navigate('/control-activities', { state: { processId: null } });
  };

  // Show 404 if control not found in edit mode
  if (controlNotFound) {
    return (
      <PageContainer>
        <PageHeader>
          <PageTitle level={2}>Control Activities</PageTitle>
          <BackButton onClick={handleGoBack}>
            <LeftOutlined />
          </BackButton>
        </PageHeader>

        <ContentWrapper>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '400px',
            gap: '16px'
          }}>
            <div style={{ fontSize: '48px', color: '#9CA3AF' }}>404</div>
            <div style={{ fontSize: '18px', color: '#6B7280', fontWeight: 500 }}>
              Control Activity not found
            </div>
            <Button type="primary" onClick={handleGoBack}>
              Back to Control Activities
            </Button>
          </div>
        </ContentWrapper>
      </PageContainer>
    );
  }

  // Handle cycle selection from dropdown
  const handleCycleDropdownSelect = (cycle: CycleItem) => {
    setSelectedCycle(cycle);
    setSelectedProcess(null);
    setGeneratedControlCode('');
    setCycleSearchTerm('');

    // Close cycle dropdown and open process dropdown
    setCycleDropdownOpen(false);
    setTimeout(() => {
      setProcessDropdownOpen(true);
    }, 100);
  };

  // Handle process selection from dropdown - auto generate control code
  const handleProcessDropdownSelect = (process: ProcessItem) => {
    setSelectedProcess(process);
    setProcessSearchTerm('');
    setProcessDropdownOpen(false);

    // Auto-generate control code
    const controlCode = generateControlCode(process.processCode);
    setGeneratedControlCode(controlCode);
    form.setFieldsValue({ controlCode });
  };

  const assertionsTemplate = (props: Objective) => {
    if (!props.financialStatementAssertions || props.financialStatementAssertions.length === 0) {
      return <span style={{ color: '#9CA3AF' }}>-</span>;
    }

    // Tooltip content for multiple assertions
    const tooltipContent = props.financialStatementAssertions.length > 1 ? (
      <div>
        <TooltipTitle>Financial Statement Assertions</TooltipTitle>
        <TooltipList>
          {props.financialStatementAssertions.map((assertion, index) => (
            <TooltipItem key={index}>
              {assertion}
            </TooltipItem>
          ))}
        </TooltipList>
      </div>
    ) : null;

    const assertionsContent = (
      <div>
        {props.financialStatementAssertions.map((assertion, index) => (
          <AssertionChip key={index}>{assertion}</AssertionChip>
        ))}
      </div>
    );

    // Show tooltip only if multiple assertions
    if (tooltipContent) {
      return (
        <Tooltip
          title={tooltipContent}
          placement="top"
          overlayStyle={{ maxWidth: '400px' }}
          overlayInnerStyle={{
            backgroundColor: '#FFFFFF',
            padding: '16px',
            borderRadius: '12px',
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
          }}
          color="#FFFFFF"
        >
          {assertionsContent}
        </Tooltip>
      );
    }

    return assertionsContent;
  };

  // Cycle dropdown content
  const cycleDropdownContent = (
    <DropdownMenu onClick={(e) => e.stopPropagation()}>
      <SearchInput
        placeholder="Search for cycle"
        value={cycleSearchTerm}
        onChange={(e) => setCycleSearchTerm(e.target.value)}
        autoFocus
      />
      <DropdownSection>
        {cycleSearchTerm === '' && (
          <DropdownSectionLabel>All cycles</DropdownSectionLabel>
        )}
        {isSearching ? (
          <div style={{ textAlign: 'center', padding: 16 }}>
            <LoadingOutlined style={{ color: '#2563EB' }} />
          </div>
        ) : filteredCycles.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 16, color: '#9CA3AF' }}>
            No cycles found
          </div>
        ) : (
          filteredCycles.map(cycle => {
            const isSelected = selectedCycle?.cycleId === cycle.cycleId;
            return (
              <CycleDropdownItem
                key={cycle.cycleId}
                $selected={isSelected}
                onClick={() => handleCycleDropdownSelect(cycle)}
              >
                <CycleDropdownItemText>
                  {cycle.cycleCode} {cycle.cycleName}
                </CycleDropdownItemText>
                <CycleDropdownItemIcon>
                  <ChevronRightIcon />
                </CycleDropdownItemIcon>
              </CycleDropdownItem>
            );
          })
        )}
      </DropdownSection>
    </DropdownMenu>
  );

  // Process dropdown content
  const processDropdownContent = (
    <DropdownMenu onClick={(e) => e.stopPropagation()}>
      <SearchInput
        placeholder="Search for process"
        value={processSearchTerm}
        onChange={(e) => setProcessSearchTerm(e.target.value)}
        autoFocus
      />
      <DropdownSection>
        {processSearchTerm === '' && (
          <DropdownSectionLabel>All processes</DropdownSectionLabel>
        )}
        {isSearching ? (
          <div style={{ textAlign: 'center', padding: 16 }}>
            <LoadingOutlined style={{ color: '#2563EB' }} />
          </div>
        ) : filteredProcesses.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 16, color: '#9CA3AF' }}>
            {!selectedCycle ? 'Select a cycle first' : 'No processes found'}
          </div>
        ) : (
          filteredProcesses.map(process => {
            const isSelected = selectedProcess?.processId === process.processId;
            return (
              <ProcessDropdownItem key={process.processId} onClick={() => handleProcessDropdownSelect(process)}>
                <ProcessDropdownItemChip $selected={isSelected}>
                  <ProcessDropdownItemText $selected={isSelected}>
                    {process.processCode} {process.processName}
                  </ProcessDropdownItemText>
                </ProcessDropdownItemChip>
              </ProcessDropdownItem>
            );
          })
        )}
      </DropdownSection>
    </DropdownMenu>
  );

  // Main page render
  return (
    <PageContainer>
      <PageHeader>
        <PageTitle level={2}>Control Activities</PageTitle>

        <HeaderTop>
          <BackButton onClick={objectivesSearchMode ? exitObjectivesSearchMode : handleGoBack}>
            <LeftOutlined />
          </BackButton>
          <PageTitleSection>
            <PageTitleWrapper>
              <ControlId>
                {objectivesSearchMode
                  ? 'Select Objectives'
                  : displayControlCode
                    ? `Control ID ${displayControlCode}`
                    : 'New Control'}
              </ControlId>
            </PageTitleWrapper>
            {!objectivesSearchMode && selectedProcess && (
              <ProcessOwnerChip>
                <ProcessOwnerAvatar>
                  {selectedProcess.processOwnerName?.charAt(0) || 'P'}
                </ProcessOwnerAvatar>
                Process Owner: {selectedProcess.processOwnerName}
              </ProcessOwnerChip>
            )}
          </PageTitleSection>
        </HeaderTop>

        {/* Selectors Section - only show when not in form/edit mode */}
        {!showForm && (
          <SelectorsSection>
            {/* Cycle Selector Row */}
            <SelectorRow>
              <Dropdown
                dropdownRender={() => cycleDropdownContent}
                trigger={['click']}
                placement="bottomLeft"
                open={cycleDropdownOpen}
                onOpenChange={setCycleDropdownOpen}
              >
                <CycleSelector>
                  <CycleText>
                    {selectedCycle ? `${selectedCycle.cycleCode} ${selectedCycle.cycleName}` : 'Select Cycle'}
                  </CycleText>
                  <ChevronIcon $color="#1F2937">
                    <ChevronDownIcon />
                  </ChevronIcon>
                </CycleSelector>
              </Dropdown>
            </SelectorRow>

            {/* Process Selector Row */}
            <SelectorRow>
              <Dropdown
                dropdownRender={() => processDropdownContent}
                trigger={selectedCycle ? ['click'] : []}
                placement="bottomLeft"
                disabled={!selectedCycle}
                open={processDropdownOpen}
                onOpenChange={(open) => selectedCycle && setProcessDropdownOpen(open)}
              >
                <ProcessSelectorsWrapper $disabled={!selectedCycle}>
                  {selectedProcess ? (
                    <ProcessChipDisplay>
                      {selectedProcess.processCode} {selectedProcess.processName}
                    </ProcessChipDisplay>
                  ) : (
                    <span style={{ color: '#9CA3AF', fontFamily: 'Inter', fontSize: 14 }}>
                      {selectedCycle ? 'Select Process' : 'Select a cycle first'}
                    </span>
                  )}
                  {selectedCycle && (
                    <ChevronIcon $color="#6B7280">
                      <ChevronDownIcon />
                    </ChevronIcon>
                  )}
                </ProcessSelectorsWrapper>
              </Dropdown>
            </SelectorRow>
          </SelectorsSection>
        )}
      </PageHeader>

      <ContentWrapper>
        {objectivesSearchMode ? (
          // Objectives Search Mode UI
          <ObjectivesSearchContainer>
            <ObjectivesSearchHeader>
              {/* Cycle Selector */}
              <SelectorsSection>
                <SelectorRow>
                  <Dropdown
                    dropdownRender={() => (
                      <DropdownMenu onClick={(e) => e.stopPropagation()}>
                        <SearchInput
                          placeholder="Search for cycle"
                          value={objCycleSearchTerm}
                          onChange={(e) => setObjCycleSearchTerm(e.target.value)}
                          autoFocus
                        />
                        <DropdownSection>
                          {objCycleSearchTerm === '' && (
                            <DropdownSectionLabel>All cycles</DropdownSectionLabel>
                          )}
                          {objSearchCycleResults.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: 16, color: '#9CA3AF' }}>
                              {isSearchingCycles ? 'Searching cycles...' : 'No cycles found'}
                            </div>
                          ) : (
                            objSearchCycleResults.map(cycle => (
                              <CycleDropdownItem
                                key={cycle.cycleId}
                                $selected={objSearchCycle?.cycleId === cycle.cycleId}
                                onClick={() => handleObjSearchCycleSelect(cycle)}
                              >
                                <CycleDropdownItemText>
                                  {cycle.cycleCode} {cycle.cycleName}
                                </CycleDropdownItemText>
                                <CycleDropdownItemIcon>
                                  <ChevronRightIcon />
                                </CycleDropdownItemIcon>
                              </CycleDropdownItem>
                            ))
                          )}
                        </DropdownSection>
                      </DropdownMenu>
                    )}
                    trigger={['click']}
                    placement="bottomLeft"
                    open={objCycleDropdownOpen}
                    onOpenChange={setObjCycleDropdownOpen}
                  >
                    <CycleSelector>
                      <CycleText>
                        {objSearchCycle ? `Cycle: ${objSearchCycle.cycleCode} ${objSearchCycle.cycleName}` : 'Select Cycle'}
                      </CycleText>
                      <ChevronIcon $color="#1F2937">
                        <ChevronDownIcon />
                      </ChevronIcon>
                    </CycleSelector>
                  </Dropdown>
                </SelectorRow>

                {/* Process Selector */}
                <SelectorRow>
                  <Dropdown
                    dropdownRender={() => (
                      <DropdownMenu onClick={(e) => e.stopPropagation()}>
                        <SearchInput
                          placeholder="Search for process"
                          value={objProcessSearchTerm}
                          onChange={(e) => setObjProcessSearchTerm(e.target.value)}
                          autoFocus
                        />
                        <DropdownSection>
                          {objProcessSearchTerm === '' && (
                            <DropdownSectionLabel>All processes</DropdownSectionLabel>
                          )}
                          {objSearchProcessResults.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: 16, color: '#9CA3AF' }}>
                              {!objSearchCycle ? 'Select a cycle first' : isSearchingProcesses ? 'Searching processes...' : 'No processes found'}
                            </div>
                          ) : (
                            objSearchProcessResults.map(process => (
                              <ProcessDropdownItem
                                key={process.processId}
                                onClick={() => handleObjSearchProcessSelect(process)}
                              >
                                <ProcessDropdownItemChip $selected={objSearchProcess?.processId === process.processId}>
                                  <ProcessDropdownItemText $selected={objSearchProcess?.processId === process.processId}>
                                    {process.processCode} {process.processName}
                                  </ProcessDropdownItemText>
                                </ProcessDropdownItemChip>
                              </ProcessDropdownItem>
                            ))
                          )}
                        </DropdownSection>
                      </DropdownMenu>
                    )}
                    trigger={objSearchCycle ? ['click'] : []}
                    placement="bottomLeft"
                    disabled={!objSearchCycle}
                    open={objProcessDropdownOpen}
                    onOpenChange={(open) => objSearchCycle && setObjProcessDropdownOpen(open)}
                  >
                    <ProcessSelectorsWrapper $disabled={!objSearchCycle}>
                      {objSearchProcess ? (
                        <ProcessChipDisplay>
                          {objSearchProcess.processCode} {objSearchProcess.processName}
                        </ProcessChipDisplay>
                      ) : (
                        <span style={{ color: '#9CA3AF', fontFamily: 'Inter', fontSize: 14 }}>
                          {objSearchCycle ? 'Select Process' : 'Select a cycle first'}
                        </span>
                      )}
                      {objSearchCycle && (
                        <ChevronIcon $color="#6B7280">
                          <ChevronDownIcon />
                        </ChevronIcon>
                      )}
                    </ProcessSelectorsWrapper>
                  </Dropdown>
                </SelectorRow>
              </SelectorsSection>
            </ObjectivesSearchHeader>

            {/* Objectives Grid */}
            {objSearchLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
                <LoadingOutlined style={{ fontSize: 32, color: '#2563EB' }} />
              </div>
            ) : objSearchObjectives.length === 0 ? (
              <EmptyStateContainer>
                <EmptyStateText>
                  {!objSearchProcess ? 'Select a process to view objectives' : 'No objectives found for this process'}
                </EmptyStateText>
              </EmptyStateContainer>
            ) : (
              <ObjectivesGridWrapper>
                <GridComponent
                  key={`objectives-search-grid-${objSearchObjectives.length}`}
                  dataSource={objSearchObjectives}
                  allowPaging={false}
                  enableHover={true}
                >
                  <ColumnsDirective>
                    <ColumnDirective
                      headerText=''
                      width='50'
                      textAlign='Center'
                      template={(obj: any) => (
                        <CheckboxCell>
                          <Checkbox
                            checked={isObjectiveSelected(obj.id)}
                            onChange={() => toggleObjectiveSelection(obj)}
                          />
                        </CheckboxCell>
                      )}
                    />
                    <ColumnDirective
                      field='index'
                      headerText='#'
                      width='70'
                      textAlign='Center'
                      template={(obj: any) => (
                        <IndexCell>
                          {obj.index.toString().padStart(2, '0')}
                        </IndexCell>
                      )}
                    />
                    <ColumnDirective
                      field='objectives'
                      headerText='Objectives'
                      textAlign='Left'
                      template={(obj: any) => (
                        <CellContent dangerouslySetInnerHTML={{ __html: obj.objectives || '' }} />
                      )}
                    />
                    <ColumnDirective
                      field='risks'
                      headerText='Risks'
                      textAlign='Left'
                      template={(obj: any) => (
                        <CellContent dangerouslySetInnerHTML={{ __html: obj.risks || '' }} />
                      )}
                    />
                    <ColumnDirective
                      field='controlRiskRanking'
                      headerText='Objective Risk Ranking'
                      width='180'
                      textAlign='Left'
                      template={(obj: any) => (
                        <RiskChip $risk={obj.controlRiskRanking}>
                          {obj.controlRiskRanking}
                        </RiskChip>
                      )}
                    />
                    <ColumnDirective
                      field='financialStatementAssertions'
                      headerText='Assertions'
                      width='200'
                      textAlign='Left'
                      template={assertionsTemplate}
                    />
                  </ColumnsDirective>
                  <Inject services={[Page]} />
                </GridComponent>
              </ObjectivesGridWrapper>
            )}
          </ObjectivesSearchContainer>
        ) : !showForm ? (
          // Empty state - prompt user to select cycle and process
          <EmptyStateContainer>
            <EmptyStateText>
              {!selectedCycle
                ? 'Select a cycle to continue'
                : 'Select a process to create control'
              }
            </EmptyStateText>
            <EmptyStateSubtext>
              Use the dropdowns above to select the cycle and process for this control activity.
            </EmptyStateSubtext>
          </EmptyStateContainer>
        ) : (
          <Form form={form} layout="vertical" requiredMark={false}>
            <StyledTabs activeKey={activeTab} onChange={setActiveTab}>
              <Tabs.TabPane tab={<TabLabel><RankingIcon />Control Description</TabLabel>} key="1">
                <TabContent>
                  {/* Tab 1: Control Description Fields */}
                  <SectionTitle>Control Description</SectionTitle>

                  {/* Identity Section */}
                  <IdentitySection>
                    <IdentityRow>
                      <IdentityLabel>Cycle ID</IdentityLabel>
                      <CycleChip>{selectedCycle?.cycleCode} {selectedCycle?.cycleName}</CycleChip>
                    </IdentityRow>

                    <IdentityRow>
                      <IdentityLabel>Process ID</IdentityLabel>
                      <ProcessChip>{selectedProcess?.processCode} {selectedProcess?.processName}</ProcessChip>
                    </IdentityRow>

                    <IdentityRow>
                      <IdentityLabel>Control ID</IdentityLabel>
                      <ControlIdChip>{displayControlCode}</ControlIdChip>
                    </IdentityRow>
                  </IdentitySection>

                  {/* Hidden field for controlCode */}
                  <Form.Item name="controlCode" noStyle hidden>
                    <Input type="hidden" />
                  </Form.Item>

                  {/* 6 Ws Control Description - framed section */}
                  <ControlDescriptionFrame>
                    <FieldRow>
                      <FieldLabel>What <RequiredMark>*</RequiredMark></FieldLabel>
                      <Form.Item name="what" noStyle style={{ flex: 1 }}>
                        <StyledTextArea rows={2} placeholder="Enter control description..." spellCheck={true} />
                      </Form.Item>
                    </FieldRow>

                    <FieldRow>
                      <FieldLabel>When <RequiredMark>*</RequiredMark></FieldLabel>
                      <Form.Item name="when" noStyle style={{ flex: 1 }}>
                        <StyledSelect placeholder="Select frequency">
                          {frequencyOptions.map((opt) => (
                            <Select.Option key={opt.value} value={opt.value}>{opt.label}</Select.Option>
                          ))}
                        </StyledSelect>
                      </Form.Item>
                    </FieldRow>

                    <FieldRow>
                      <FieldLabel>Who <RequiredMark>*</RequiredMark></FieldLabel>
                      <Form.Item name="who" noStyle style={{ flex: 1 }}>
                        <StyledTextArea rows={1} placeholder="Enter performer..." spellCheck={true} />
                      </Form.Item>
                    </FieldRow>

                    <FieldRow>
                      <FieldLabel>Why <RequiredMark>*</RequiredMark></FieldLabel>
                      <Form.Item name="why" noStyle style={{ flex: 1 }}>
                        <StyledTextArea rows={2} placeholder="Enter assertion(s)..." spellCheck={true} />
                      </Form.Item>
                    </FieldRow>

                    <FieldRow>
                      <FieldLabel>How <RequiredMark>*</RequiredMark></FieldLabel>
                      <Form.Item name="how" noStyle style={{ flex: 1 }}>
                        <StyledTextArea rows={2} placeholder="Enter procedure..." spellCheck={true} />
                      </Form.Item>
                    </FieldRow>

                    <FieldRow>
                      <FieldLabel>Evidence <RequiredMark>*</RequiredMark></FieldLabel>
                      <Form.Item name="evidence" noStyle style={{ flex: 1 }}>
                        <StyledTextArea rows={2} placeholder="Enter evidence..." spellCheck={true} />
                      </Form.Item>
                    </FieldRow>
                  </ControlDescriptionFrame>

                  <Section>
                    {/* Classification Section */}
                    <FieldRow>
                      <FieldLabel>Category <RequiredMark>*</RequiredMark></FieldLabel>
                      <Form.Item name="category" noStyle style={{ flex: 1 }}>
                        <StyledSelect placeholder="Select category">
                          {categoryOptions.map((opt) => (
                            <Select.Option key={opt.value} value={opt.value}>{opt.label}</Select.Option>
                          ))}
                        </StyledSelect>
                      </Form.Item>
                    </FieldRow>

                    <FieldRow>
                      <FieldLabel>Type <RequiredMark>*</RequiredMark></FieldLabel>
                      <Form.Item name="controlType" noStyle style={{ flex: 1 }}>
                        <StyledSelect placeholder="Select type">
                          {controlTypeOptions.map((opt) => (
                            <Select.Option key={opt.value} value={opt.value}>{opt.label}</Select.Option>
                          ))}
                        </StyledSelect>
                      </Form.Item>
                    </FieldRow>

                    <FieldRow>
                      <FieldLabel>Key / Non-Key <RequiredMark>*</RequiredMark></FieldLabel>
                      <Form.Item name="isKeyControl" noStyle style={{ flex: 1 }}>
                        <StyledSelect placeholder="Select">
                          <Select.Option value={true}>Key</Select.Option>
                          <Select.Option value={false}>Non-Key</Select.Option>
                        </StyledSelect>
                      </Form.Item>
                    </FieldRow>

                    <FieldRow>
                      <FieldLabel>Objectives</FieldLabel>
                      <ObjectivesFieldWrapper onClick={enterObjectivesSearchMode}>
                        {selectedObjectives.length > 0 ? (
                          <>
                            {/* Group objectives by process code */}
                            {Object.entries(
                              selectedObjectives.reduce((acc, obj) => {
                                if (!acc[obj.processCode]) {
                                  acc[obj.processCode] = [];
                                }
                                acc[obj.processCode].push(obj);
                                return acc;
                              }, {} as Record<string, SelectedObjective[]>)
                            ).map(([processCode, objectives]) => (
                              <SelectedObjectiveTag key={processCode}>
                                <ObjectiveProcessCode>{processCode}</ObjectiveProcessCode>
                                <ObjectiveNumbersGroup>
                                  {[...objectives].sort((a, b) => {
                                    const numA = parseInt(a.objectiveNumber, 10);
                                    const numB = parseInt(b.objectiveNumber, 10);
                                    return numA - numB;
                                  }).map((obj) => (
                                    <ObjectiveNumberCircle key={obj.id}>
                                      {obj.objectiveNumber}
                                    </ObjectiveNumberCircle>
                                  ))}
                                </ObjectiveNumbersGroup>
                              </SelectedObjectiveTag>
                            ))}
                          </>
                        ) : (
                          <span style={{ color: '#9CA3AF', fontFamily: 'Inter', fontSize: 14 }}>
                            Select objectives...
                          </span>
                        )}
                      </ObjectivesFieldWrapper>
                      <ObjectivesExpandIcon onClick={enterObjectivesSearchMode}>
                        <RightOutlined />
                      </ObjectivesExpandIcon>
                      {/* Hidden form field for objectiveIds */}
                      <Form.Item name="objectiveIds" noStyle hidden>
                        <Input type="hidden" />
                      </Form.Item>
                    </FieldRow>

                    <FieldRow key={`risk-rank-${riskRank}`}>
                      <FieldLabel $isDisabled>Risk Rank <ReadOnlyBadge>read only</ReadOnlyBadge></FieldLabel>
                      <RiskChip $risk={riskRank}>{riskRank}</RiskChip>
                    </FieldRow>

                    <FieldRow key={`assertions-${assertions.length}`}>
                      <FieldLabel $isDisabled>Assertions <ReadOnlyBadge>read only</ReadOnlyBadge></FieldLabel>
                      {assertions.length > 0 ? (
                        <ChipContainer>
                          {assertions.map((assertion, idx) => (
                            <AssertionChip key={idx}>
                              {assertion}
                            </AssertionChip>
                          ))}
                        </ChipContainer>
                      ) : (
                        <StyledInput
                          disabled
                          placeholder="No assertions"
                          style={{ flex: 1 }}
                        />
                      )}
                    </FieldRow>

                    {/* Ownership Section */}
                    <FieldRow>
                      <FieldLabel>Control Owner <RequiredMark>*</RequiredMark></FieldLabel>
                      <Form.Item name="controlOwner" noStyle style={{ flex: 1 }}>
                        <StyledInput placeholder="Enter control owner..." spellCheck={true} />
                      </Form.Item>
                    </FieldRow>

                    <FieldRow>
                      <FieldLabel $isDisabled>Process Owner <ReadOnlyBadge>read only</ReadOnlyBadge></FieldLabel>
                      <StyledInput
                        disabled
                        value={selectedProcess?.processOwnerName || ''}
                        placeholder="From EX3"
                        style={{ flex: 1 }}
                      />
                    </FieldRow>

                    <FieldRow>
                      <FieldLabel>Applications</FieldLabel>
                      <StyledSelect
                        mode="multiple"
                        value={selectedApplicationIds}
                        onChange={(value) => setSelectedApplicationIds(value as string[])}
                        placeholder={isLoadingApplications ? "Loading applications..." : "Select applications..."}
                        loading={isLoadingApplications}
                        showSearch
                        filterOption={(input, option) =>
                          (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                        }
                        tagRender={renderApplicationTag}
                        options={[...applications]
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map(app => ({
                            value: app.id,
                            label: app.name,
                          }))}
                        notFoundContent={applications.length === 0 ? 'No applications available' : 'Not found'}
                      />
                    </FieldRow>
                  </Section>
                </TabContent>
              </Tabs.TabPane>

              <Tabs.TabPane tab={<TabLabel><ObjectivesIcon />Testing</TabLabel>} key="2">
                <TabContent>
                  {/* Tab 2: Testing Fields */}
                  <Section>

                    {/* Testing Scope */}
                    <FieldRow style={{ marginTop: -16 }}>
                      <FieldLabel>Testing Scope</FieldLabel>
                      <SwitchRow>
                        <TestingScopeChip $inScope={inTestingScope || false}>
                          {inTestingScope ? 'In Scope' : 'Out of Scope'}
                        </TestingScopeChip>
                        <Form.Item name="inTestingScope" valuePropName="checked" noStyle>
                          <Switch />
                        </Form.Item>
                      </SwitchRow>
                    </FieldRow>

                    {/* Control Weight */}
                    <FieldRow>
                      <FieldLabel $isDisabled={!inTestingScope}>
                        Control Weight
                        <Tooltip title="Represents the Control's impact on its parent Process. All Control weights within this Process must sum to 100%.">
                          <InfoCircleOutlined className="info-icon" />
                        </Tooltip>
                        {inTestingScope && <RequiredMark style={{ marginLeft: 4 }}>*</RequiredMark>}
                      </FieldLabel>
                        <Form.Item name="controlWeight" noStyle style={{ flex: 1 }}>
                          <StyledInput
                            type="number"
                            min={0}
                            step={1}
                            placeholder="Enter control weight..."
                            disabled={!inTestingScope}
                          />
                        </Form.Item>
                    </FieldRow>

                    {/* Testing Fields */}
                    <FieldRow>
                      <FieldLabel $isDisabled={!inTestingScope}>Test Plan {inTestingScope && <RequiredMark>*</RequiredMark>}</FieldLabel>
                      <Form.Item name="testPlan" noStyle style={{ flex: 1 }}>
                        <StyledTextArea
                          rows={3}
                          placeholder="Enter test plan..."
                          disabled={!inTestingScope}
                          spellCheck={true}
                        />
                      </Form.Item>
                    </FieldRow>

                    <FieldRow>
                      <FieldLabel $isDisabled={!inTestingScope}>Walkthrough Result {inTestingScope && <RequiredMark>*</RequiredMark>}</FieldLabel>
                      <Form.Item name="walkthroughResult" noStyle style={{ flex: 1 }}>
                        <StyledTextArea
                          rows={3}
                          placeholder="Enter walkthrough result..."
                          disabled={!inTestingScope}
                          spellCheck={true}
                        />
                      </Form.Item>
                    </FieldRow>

                    <FieldRow>
                      <FieldLabel $isDisabled={!inTestingScope}>Design Effectiveness {inTestingScope && <RequiredMark>*</RequiredMark>}</FieldLabel>
                      <Form.Item name="isDesignEffective" noStyle style={{ flex: 1 }}>
                        <StyledSelect placeholder="Select" disabled={!inTestingScope}>
                          {effectivenessOptions.map((opt) => (
                            <Select.Option key={opt.value} value={opt.value}>{opt.label}</Select.Option>
                          ))}
                        </StyledSelect>
                      </Form.Item>
                    </FieldRow>

                    <FieldRow>
                      <FieldLabel $isDisabled={!inTestingScope}>Interim Completed</FieldLabel>
                      <Form.Item name="interimCompleted" noStyle style={{ flex: 1 }}>
                        <StyledSelect placeholder="Select" disabled={!inTestingScope}>
                          {completionOptions.map((opt) => (
                            <Select.Option key={opt.value} value={opt.value}>{opt.label}</Select.Option>
                          ))}
                        </StyledSelect>
                      </Form.Item>
                    </FieldRow>

                    <FieldRow>
                      <FieldLabel $isDisabled={!inTestingScope}>Total Samples {inTestingScope && <RequiredMark>*</RequiredMark>}</FieldLabel>
                      <Form.Item name="samplesTotal" noStyle style={{ flex: 1 }}>
                        <StyledInput
                          type="number"
                          placeholder="Enter total samples..."
                          disabled={!inTestingScope}
                        />
                      </Form.Item>
                    </FieldRow>

                    <FieldRow>
                      <FieldLabel $isDisabled={!inTestingScope}>Samples Tested {inTestingScope && <RequiredMark>*</RequiredMark>}</FieldLabel>
                      <Form.Item name="samplesTested" noStyle style={{ flex: 1 }}>
                        <StyledInput
                          type="number"
                          placeholder="Enter samples tested..."
                          disabled={!inTestingScope}
                        />
                      </Form.Item>
                    </FieldRow>

                    <FieldRow>
                      <FieldLabel $isDisabled={!inTestingScope}>Attachments</FieldLabel>
                      <AttachmentContainer>
                        <StyledUpload
                          fileList={[]}
                          beforeUpload={handleAttachmentSelect}
                          showUploadList={false}
                          multiple
                          accept={ALLOWED_DOCUMENT_EXTENSIONS}
                          disabled={!inTestingScope}
                        >
                          <Button
                            icon={<UploadOutlined />}
                            disabled={!inTestingScope}
                          >
                            Select Files
                          </Button>
                        </StyledUpload>
                        {attachments.length > 0 && (
                          <FileListWrapper>
                            {attachments.map((fileSelect, index) => (
                              <FileItem key={fileSelect.fileId || `new-${index}`}>
                                {fileSelect.fileId && 
                                    <FileLink className="file-name" title={fileSelect.name} onClick={(event:React.MouseEvent) => handleDownload(event, fileSelect)}>
                                        {isDownloading ? (
                                            <LoadingOutlined style={{ fontSize: 12 }} spin />
                                        ) : (
                                            <LinkIcon />
                                        )}
                                        {fileSelect.name}
                                    </FileLink>
                                }
                                {!fileSelect.fileId && 
                                    <FileNoLink className="file-name" title={fileSelect.name}>
                                        {fileSelect.name + ' (new)'}
                                    </FileNoLink>
                                }
                                <StyledCloseIcon
                                  $circleFill="#F8FAFC"
                                  $strokeColor="#1E40AF"
                                  onClick={() => handleAttachmentDelete(fileSelect)}
                                />
                              </FileItem>
                            ))}
                          </FileListWrapper>
                        )}
                      </AttachmentContainer>
                    </FieldRow>

                    <FieldRow>
                      <FieldLabel $isDisabled>Testing Status <ReadOnlyBadge>read only</ReadOnlyBadge></FieldLabel>
                      <StyledInput
                        disabled
                        value={testingStatusDisplay}
                        style={{ flex: 1 }}
                      />
                    </FieldRow>

                    <FieldRow>
                      <FieldLabel $isDisabled>Final Status <ReadOnlyBadge>read only</ReadOnlyBadge></FieldLabel>
                      <StyledInput
                        disabled
                        value={calculatedFinalStatus}
                        style={{ flex: 1 }}
                      />
                    </FieldRow>

                    <FieldRow>
                      <FieldLabel $isDisabled={!inTestingScope}>Operating Effectiveness {inTestingScope && <RequiredMark>*</RequiredMark>}</FieldLabel>
                      <Form.Item name="operatingEffectiveness" noStyle style={{ flex: 1 }}>
                        <StyledSelect placeholder="Select" disabled={!inTestingScope}>
                          {effectivenessOptions.map((opt) => (
                            <Select.Option key={opt.value} value={opt.value}>{opt.label}</Select.Option>
                          ))}
                        </StyledSelect>
                      </Form.Item>
                    </FieldRow>

                    <FieldRow>
                      <FieldLabel $isDisabled={!inTestingScope}>Completion Date</FieldLabel>
                      <Form.Item name="completionDate" noStyle style={{ flex: 1 }}>
                        <DatePicker
                          style={{ width: '100%', height: 44, borderRadius: 8 }}
                          placeholder="Select date"
                          format="YYYY-MM-DD"
                          disabled={!inTestingScope}
                        />
                      </Form.Item>
                    </FieldRow>

                    <FieldRow>
                      <FieldLabel $isDisabled={!inTestingScope}>Auditor</FieldLabel>
                      <Form.Item name="auditorId" noStyle style={{ flex: 1 }}>
                        <StyledSelect
                          placeholder="Select auditor"
                          disabled={!inTestingScope}
                          allowClear
                          showSearch
                          filterOption={(input, option) => {
                            const auditor = auditors.find(a => a.id === option?.value);
                            return auditor?.name.toLowerCase().includes(input.toLowerCase()) || false;
                          }}
                          labelRender={(props) => {
                            const auditor = auditors.find(a => a.id === props.value);
                            if (!auditor) return props.label;
                            return (
                              <SelectAuditorCell>
                                <SelectAvatar>{getUserInitials(auditor.name)}</SelectAvatar>
                                <SelectName>{auditor.name}</SelectName>
                              </SelectAuditorCell>
                            );
                          }}
                          options={auditors.map(a => ({
                            value: a.id,
                            label: (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '4px 0' }}>
                                <SelectAvatar>{getUserInitials(a.name)}</SelectAvatar>
                                <div>
                                  <div style={{ fontWeight: 500 }}>{a.name}</div>
                                </div>
                              </div>
                            ),
                          }))}
                        />
                      </Form.Item>
                    </FieldRow>

                    {isAdmin && (
                      <FieldRow>
                        <FieldLabel $isDisabled={!inTestingScope}>Budget Hours</FieldLabel>
                        <Form.Item name="budgetHours" noStyle style={{ flex: 1 }}>
                          <StyledInput
                            type="number"
                            min={0}
                            disabled={!inTestingScope}
                            placeholder="Enter budget hours..."
                          />
                        </Form.Item>
                      </FieldRow>
                    )}
                  </Section>
                </TabContent>
              </Tabs.TabPane>
            </StyledTabs>
          </Form>
        )}
      </ContentWrapper>

      {objectivesSearchMode ? (
        <Footer>
          <ButtonGroup>
            <StyledOutlinedButton
              type="default"
              onClick={exitObjectivesSearchMode}
            >
              Cancel
            </StyledOutlinedButton>
            <StyledFilledButton
              type="primary"
              onClick={confirmObjectivesSelection}
            >
              Done ({tempSelectedObjectives.length} selected)
            </StyledFilledButton>
          </ButtonGroup>
        </Footer>
      ) : showForm && (
        <Footer>
          <ButtonGroup>
            {mode === 'edit' && controlData?.status === 'Active' ? (
              // Edit Active: Save only
              <StyledFilledButton
                type="primary"
                onClick={handleSaveAsActive}
                disabled={!isValidForActive || !isAdmin}
                loading={isSavingAsActive}
              >
                Save
              </StyledFilledButton>
            ) : (
              // Add mode or Edit Draft: Save as Draft + Save
              <>
                <StyledOutlinedButton
                  type="default"
                  onClick={handleSaveAsDraft}
                  disabled={!isValidForDraft || isSavingAsDraft || !isAdmin}
                  loading={isSavingAsDraft}
                >
                  Save as Draft
                </StyledOutlinedButton>
                <StyledFilledButton
                  type="primary"
                  onClick={handleSaveAsActive}
                  disabled={!isValidForActive || isSavingAsActive || !isAdmin}
                  loading={isSavingAsActive}
                >
                  Save
                </StyledFilledButton>
              </>
            )}
          </ButtonGroup>
        </Footer>
      )}

      {/* GAP Pre-Save Confirmation Modal */}
      <ConfirmModal
        open={gapAutoCreateModalOpen}
        onClose={handleGapAutoCreateDecline}
        onConfirm={handleGapAutoCreateConfirm}
        title="Ineffective Control Detected"
        description="This control has been marked as ineffective. Would you like to automatically raise a Draft GAP for this deficiency?"
        confirmText="Yes, Create GAP"
        cancelText="No, Just Save Control"
      />

      {/* GAP Post-Save Prompt Modal (view created GAP) */}
      <ConfirmModal
        open={gapPromptOpen}
        onClose={handleDismissGapPrompt}
        onConfirm={handleViewGap}
        title="Draft Gap Created"
        description="A Draft Gap has been automatically created for this deficiency. Do you want to view and edit the details now?"
        confirmText="Yes, View Gap"
        cancelText="No"
      />
    </PageContainer>
  );
};

export default ControlActivityItemPage;
